import React, { useState } from 'react'
import { useGetBusiness } from '@marketing-milk/frontend'
import { AdCreative, AssetApprovalStatus, LeadCard } from '@marketing-milk/interfaces'
import { Popover } from '@mui/material'
import { EmptyLeadCardId } from 'components'
import { AdCreativeList } from 'pages/business/campaign-builder/steps/ad-creative/ad-creative-list/AdCreativeList'
import { LeadCardPreview } from 'pages/business/lead-cards/lead-card-preview/LeadCardPreview'
import { leadCardDTOWire } from 'pages/business/lead-cards/wizard/lead-card.wire'

type Props = {
  campaignName?: string
  creatives?: AdCreative[]
  declaredForms: LeadCard[]
  activeFormId: number
  setActiveFormId: (id: number) => void
  selectedCreatives: AdCreative[]
  selectCreative: (creative: AdCreative) => void
  selectAll: (visible: AdCreative[]) => void
  deselectAll: (visible: AdCreative[]) => void
  onSelectLeadless: (visible: AdCreative[]) => void
  onAssign: () => void
  assigning: boolean
  onUnassign: () => void
  unassigning: boolean
}

export const AssignLeadFormsStep = ({
  campaignName,
  creatives,
  declaredForms,
  activeFormId,
  setActiveFormId,
  selectedCreatives,
  selectCreative,
  selectAll,
  deselectAll,
  onSelectLeadless,
  onAssign,
  assigning,
  onUnassign,
  unassigning,
}: Props) => {
  const business = useGetBusiness()
  const [previewAnchor, setPreviewAnchor] = useState<HTMLElement>()
  const unassignedCount = (creatives ?? []).filter(c => !c.leadCardUsed).length
  const countForForm = (form: LeadCard) =>
    (creatives ?? []).filter(c => c.leadCardUsed?.id === form.id).length
  const activeForm = declaredForms.find(f => f.id === activeFormId)
  // Undo for a bad assignment: send just the selected ads back to Unassigned, regardless of
  // which rail is active. Only meaningful when at least one selected ad actually carries a form.
  const selectedHaveAForm = selectedCreatives.some(c => c.leadCardUsed)

  // Deliberately NOT wrapped in BlankPageLayout: its content area centers children with
  // `items-center`, which lets a block child shrink to its content's natural width instead of
  // stretching — fine for short text, but it broke AdCreativeList's fixed-width card grid (rows
  // overlapped once nested here). Plain block markup keeps every width explicit.
  //
  // No max-w cap here either: AdCreativeList's columns are a fixed pixel width apiece
  // (grid-cols-N switches on viewport size, not container size), so capping this wrapper
  // narrower than the Drawer actually is starves the grid of width it has plenty of, and the
  // fixed-width cards overflow their tracks into their neighbors.
  //
  // The lead-card preview is a Popover (portal-rendered, out of normal flow) rather than a flex
  // sibling of the grid, for the same reason — a sibling column would shrink the grid's width
  // without shrinking its column count.
  return (
    <div className="w-full p-4 space-y-4">
      <h1 className="font-lg font-bold mx-auto text-center">
        {campaignName ? `Assign Ads To Forms — ${campaignName}` : 'Assign Ads To Forms'}
      </h1>
      <p className="font-medium mx-auto max-w-2xl font-md text-gray-600 text-center">
        Select the ads below, then pick a form from the rail to attach it to all of them at once.
        Published ads are locked — fix those in Ads Manager instead.
      </p>

      <div className="flex flex-wrap gap-2 justify-center">
        <button
          type="button"
          onClick={() => {
            setActiveFormId(EmptyLeadCardId)
            setPreviewAnchor(undefined)
          }}
          className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
            activeFormId === EmptyLeadCardId
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          Unassigned ({unassignedCount})
        </button>
        {declaredForms.map(form => (
          <button
            key={form.id}
            type="button"
            onClick={event => {
              setActiveFormId(form.id)
              setPreviewAnchor(event.currentTarget)
            }}
            className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
              activeFormId === form.id
                ? 'bg-purple-700 text-white border-purple-700'
                : 'bg-white text-purple-700 border-purple-600 hover:bg-purple-50'
            }`}
          >
            {form.formName} ({countForForm(form)})
          </button>
        ))}
      </div>

      <AdCreativeList
        creatives={creatives}
        selectedCreatives={selectedCreatives}
        selectCreative={selectCreative}
        onSelectAll={selectAll}
        onDeselectAll={deselectAll}
        onSelectLeadless={onSelectLeadless}
        isHighlighted={c =>
          activeFormId === EmptyLeadCardId ? !c.leadCardUsed : c.leadCardUsed?.id === activeFormId
        }
        disableSelect={c => c.reviewStatus === AssetApprovalStatus.Published}
        defaultViewMode="list"
      />

      <Popover
        open={!!activeForm && !!previewAnchor}
        anchorEl={previewAnchor}
        onClose={() => setPreviewAnchor(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {activeForm && (
          <div className="p-3">
            <p className="font-semibold text-sm text-gray-600 mb-2 truncate">
              Previewing: {activeForm.formName}
            </p>
            <LeadCardPreview
              leadCard={leadCardDTOWire(activeForm)}
              business={business}
              mobilePreview={false}
              selectionPreview={false}
              showName={false}
            />
          </div>
        )}
      </Popover>

      {/* Sticky rather than at the natural end of the scrolling content — with a batch of 50
          ads the button used to scroll out of reach below the grid. `bottom-0` sticks it to
          the bottom of AssignLeadFormsStep's scrolling ancestor (Stepper's own StepContent),
          landing it right above the Stepper's Previous/Done footer, always in view. */}
      <div className="sticky bottom-0 z-10 -mx-4 border-t bg-white px-4 py-4 flex items-center justify-center gap-4">
        <button
          className="p-8 bg-purple-200 border border-dashed border-gray-300 hover:bg-gray-100 flex items-center justify-center font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onAssign}
          disabled={assigning || activeFormId === EmptyLeadCardId || selectedCreatives.length === 0}
        >
          {assigning
            ? 'Assigning...'
            : `Assign ${activeForm ? `"${activeForm.formName}"` : 'Form'} to ${
                selectedCreatives.length
              } Selected Ad${selectedCreatives.length === 1 ? '' : 's'}`}
        </button>
        <button
          className="p-8 bg-white border border-dashed border-gray-300 hover:bg-gray-100 flex items-center justify-center font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onUnassign}
          disabled={unassigning || !selectedHaveAForm}
        >
          {unassigning
            ? 'Removing...'
            : `Remove Lead Form From ${selectedCreatives.length} Selected Ad${
                selectedCreatives.length === 1 ? '' : 's'
              }`}
        </button>
      </div>
    </div>
  )
}
